export const PHOENIX_VENUE_ID = "phoenix-heights";
export const PHOENIX_TIMEZONE = "Europe/London";

export function configuredPolicyUrl(value = "") {
  const candidate = String(value || "").trim();
  return candidate.startsWith("https://") || candidate.startsWith("/") ? candidate : "";
}

export const phoenixHubContent = {
  publicName: "Phoenix Hub",
  formalName: "Phoenix Heights Community Centre",
  eyebrow: "A new UUN community venue",
  introduction:
    "Phoenix Hub is a welcoming community centre serving residents, organisations and local partners on the Isle of Dogs. The centre offers flexible indoor activity spaces and a rooftop sports pitch for classes, meetings, workshops, community programmes, celebrations and sporting activities.",
  bookingNotice:
    "Submitting this form does not confirm your booking. A member of the Urban Unity Network team will review your request, confirm availability, provide the final price and explain the next steps.",
  pricingNote:
    "Prices are indicative and may vary depending on the activity, duration, frequency, staffing, equipment, cleaning requirements and commercial or community use.",
  successMessage:
    "Thank you for submitting your Phoenix Hub booking request. Your booking is not yet confirmed. The Urban Unity Network team will review your request, check availability and contact you with the final price and next steps.",
  enquiryEmail: process.env.REACT_APP_PHOENIX_ENQUIRY_EMAIL || "",
  termsUrl: configuredPolicyUrl(process.env.REACT_APP_PHOENIX_TERMS_URL),
  cancellationUrl: configuredPolicyUrl(process.env.REACT_APP_PHOENIX_CANCELLATION_URL),
  privacyUrl: configuredPolicyUrl(process.env.REACT_APP_PHOENIX_PRIVACY_URL),
  safeguardingUrl: configuredPolicyUrl(process.env.REACT_APP_PHOENIX_SAFEGUARDING_URL),
  centreRulesUrl: configuredPolicyUrl(process.env.REACT_APP_PHOENIX_CENTRE_RULES_URL),
  documentUploadsEnabled:
    process.env.REACT_APP_PHOENIX_DOCUMENT_UPLOADS_ENABLED === "true",
  maxUploadBytes: 10 * 1024 * 1024,
};

export const supportedActivities = [
  "Community meetings",
  "Training and workshops",
  "Exercise and wellbeing sessions",
  "Youth activities",
  "Children’s programmes",
  "Sports sessions",
  "Advice and support services",
  "Classes and clubs",
  "Private and community events",
  "Recurring group bookings",
];

export const defaultPhoenixSpaces = [
  {
    id: "ground-floor-community-space",
    venueId: PHOENIX_VENUE_ID,
    name: "Ground Floor Community Space",
    slug: "ground-floor-community-space",
    description:
      "A welcoming, adaptable room for neighbourhood meetings, advice sessions, workshops and smaller community gatherings.",
    location: "Ground floor",
    capacity: 30,
    hourlyRate: 25,
    rateLabel: "From £25 per hour",
    suitableActivities: [
      "Meetings",
      "Community activities",
      "Advice sessions",
      "Workshops",
      "Small events",
      "Parent and child sessions",
    ],
    facilities: ["Flexible furniture layout", "Wi-Fi", "Kitchenette access"],
    accessibility:
      "Step-free ground-floor access. Please tell us about any access requirements when enquiring.",
    images: [],
    active: true,
    displayOrder: 1,
  },
  {
    id: "first-floor-community-space",
    venueId: PHOENIX_VENUE_ID,
    name: "First Floor Community Space",
    slug: "first-floor-community-space",
    description:
      "A flexible indoor room for meetings, classes, workshops, group activities and smaller community events.",
    location: "First floor",
    capacity: 30,
    hourlyRate: 35,
    rateLabel: "From £35 per hour",
    suitableActivities: [
      "Meetings",
      "Classes",
      "Training",
      "Workshops",
      "Group activities",
      "Small events",
    ],
    facilities: ["Flexible furniture layout", "Wi-Fi", "Kitchenette access"],
    accessibility:
      "Please contact UUN to confirm lift access and the suitability of this space for your group.",
    images: [],
    active: true,
    displayOrder: 2,
  },
  {
    id: "second-floor-activity-space",
    venueId: PHOENIX_VENUE_ID,
    name: "Second Floor Activity Space",
    slug: "second-floor-activity-space",
    description:
      "A larger indoor room suited to movement, learning and regular group programmes, with space for active sessions.",
    location: "Second floor",
    capacity: 50,
    hourlyRate: 45,
    rateLabel: "From £45 per hour",
    suitableActivities: [
      "Exercise sessions",
      "Dance",
      "Martial arts",
      "Training",
      "Youth activities",
      "Larger workshops",
      "Regular classes",
    ],
    facilities: ["Open activity floor", "Wi-Fi", "Flexible furniture layout"],
    accessibility:
      "Please contact UUN to confirm lift access and the suitability of this space for your group.",
    images: [],
    active: true,
    displayOrder: 3,
  },
  {
    id: "rooftop-sports-pitch",
    venueId: PHOENIX_VENUE_ID,
    name: "Rooftop Football Pitch",
    slug: "rooftop-sports-pitch",
    description:
      "An outdoor rooftop football pitch for coached sport, youth sessions, fitness and community competitions.",
    location: "Rooftop",
    capacity: 30,
    hourlyRate: 45,
    rateLabel: "From £45 per hour",
    suitableActivities: [
      "Football",
      "Sports coaching",
      "Youth sessions",
      "Fitness activities",
      "Community tournaments",
    ],
    facilities: ["Sports surface", "Secure perimeter", "Changing access by arrangement"],
    accessibility:
      "Rooftop access arrangements should be discussed with UUN before booking.",
    images: [],
    active: true,
    displayOrder: 4,
  },
  {
    id: "whole-centre",
    venueId: PHOENIX_VENUE_ID,
    name: "Whole Centre or Multi-Space Hire",
    slug: "whole-centre",
    description:
      "Combine rooms and activity areas for larger events, coordinated programmes and partner-led delivery.",
    location: "Multiple floors",
    capacity: null,
    hourlyRate: null,
    rateLabel: "Available on request",
    suitableActivities: [
      "Larger events",
      "Multi-session programmes",
      "Community festivals",
      "Organisation-wide activities",
      "Partner-led programmes",
    ],
    facilities: ["Multi-space planning", "Flexible setup", "Staffing options by arrangement"],
    accessibility:
      "UUN will create an access and movement plan with organisers of whole-centre events.",
    images: [],
    active: true,
    displayOrder: 5,
  },
];

export const BOOKING_STATUS_OPTIONS = [
  ["submitted", "Submitted"],
  ["under_review", "Under review"],
  ["more_information_required", "More information required"],
  ["provisional_hold", "Provisional hold"],
  ["approved_awaiting_payment", "Approved — awaiting payment"],
  ["confirmed", "Confirmed"],
  ["rejected", "Rejected"],
  ["cancelled", "Cancelled"],
  ["completed", "Completed"],
];

export const BOOKING_STATUS_LABELS = Object.fromEntries(BOOKING_STATUS_OPTIONS);

export const PAYMENT_STATUS_OPTIONS = [
  ["not_requested", "Not requested"],
  ["payment_requested", "Payment requested"],
  ["partially_paid", "Partially paid"],
  ["paid", "Paid"],
  ["refunded", "Refunded"],
  ["waived", "Waived"],
];

export const BLOCK_TYPES = [
  "Maintenance",
  "UUN programme",
  "Private closure",
  "Holiday closure",
  "Setup period",
  "Cleaning",
  "Whole-centre closure",
];

export const ACCEPTED_DOCUMENT_TYPES = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};
